import React from 'react';
import { Link } from 'react-router-dom';
import BlogArticleLayout, { blogStyles as s } from './BlogArticleLayout';

function BlogArticleCybersecuritePME() {
  return (
    <BlogArticleLayout
      tag="Cybersécurité"
      title="Cybersécurité PME : le guide complet pour les dirigeants"
      date="8 septembre 2026"
      ctaTitle="Où en est votre entreprise aujourd'hui ?"
      ctaText="Le diagnostic gratuit passe en revue vos pratiques actuelles et vous donne un score clair, avec un plan d'action pour progresser."
    >
      <p style={s.intro}>
        Ransomware, phishing, fuite de données : ces mots reviennent régulièrement dans l'actualité, mais
        restent souvent perçus comme des sujets réservés aux grandes entreprises ou aux équipes informatiques.
        Pour une PME ou une ETI, c'est une erreur de perspective : la taille de l'entreprise ne protège pas du
        risque, elle change simplement sa nature. Ce guide fait le point sur ce qu'un dirigeant doit
        comprendre, sans prérequis technique.
      </p>

      <h2 style={s.h2}>Pourquoi la cybersécurité est devenue un sujet de dirigeant</h2>
      <p>
        Longtemps, la cybersécurité a été traitée comme un sujet purement technique, délégué à un prestataire
        informatique ou à un service IT quand il existe. Le problème, c'est qu'une cyberattaque n'a pas des
        conséquences techniques : elle a des conséquences business — arrêt d'activité, perte de données
        clients, atteinte à la réputation, obligations légales de notification, voire mise en cause de la
        responsabilité du dirigeant. C'est un risque d'entreprise au même titre qu'un risque financier ou
        juridique, ce qui justifie qu'il soit piloté au niveau de la direction, pas seulement délégué.
      </p>

      <h2 style={s.h2}>Les risques principaux pour une PME</h2>
      <ul style={s.ul}>
        <li style={s.li}><strong>Le ransomware</strong> : un logiciel malveillant chiffre les données de l'entreprise et exige une rançon pour les débloquer. C'est aujourd'hui l'une des menaces les plus coûteuses pour les PME, souvent via un simple email piégé.</li>
        <li style={s.li}><strong>Le phishing</strong> : un email ou un message qui imite un interlocuteur légitime (banque, fournisseur, dirigeant) pour obtenir un virement, des identifiants ou des informations confidentielles.</li>
        <li style={s.li}><strong>La fuite de données</strong> : perte ou vol de données clients, salariés ou commerciales, par erreur humaine, mauvaise configuration ou intrusion.</li>
        <li style={s.li}><strong>La dépendance à un prestataire</strong> : beaucoup de PME confient leur informatique à un tiers sans visibilité claire sur les mesures de sécurité réellement en place.</li>
      </ul>

      <h2 style={s.h2}>Ce que « être protégé » veut dire concrètement</h2>
      <p>
        Être protégé ne se résume pas à installer un antivirus. Cela suppose un ensemble cohérent de mesures :
        des sauvegardes régulières et testées (une sauvegarde qu'on n'a jamais essayé de restaurer n'est pas
        une garantie), une gestion rigoureuse des accès (qui peut accéder à quoi, et pourquoi), une
        sensibilisation des équipes aux tentatives de phishing, et une procédure claire de réaction en cas
        d'incident — savoir qui prévenir et quoi faire dans les premières heures fait souvent la différence
        entre un incident maîtrisé et une crise.
      </p>

      <h2 style={s.h2}>Cybersécurité et conformité : deux sujets liés mais différents</h2>
      <p>
        La cybersécurité, au sens opérationnel, et la conformité réglementaire (RGPD, NIS2, ISO 27001) se
        recoupent largement mais ne sont pas identiques : on peut être relativement bien protégé sans être
        formellement conforme, et inversement. Notre article sur les
        {' '}<Link to="/blog/rgpd-nis2-iso27001-differences" style={s.link}>différences entre RGPD, NIS2 et ISO 27001</Link>{' '}
        détaille ce que chaque référentiel exige précisément.
      </p>

      <h2 style={s.h2}>Comment évaluer votre niveau actuel</h2>
      <p>
        Avant d'investir dans des outils ou de lancer un chantier de mise en conformité, la première étape
        est d'obtenir une photographie fiable de votre situation actuelle. C'est l'objet d'un audit ou d'un
        diagnostic cybersécurité, que nous détaillons dans notre
        {' '}<Link to="/blog/audit-cybersecurite-pme-guide-pratique" style={s.link}>guide pratique de l'audit cybersécurité</Link>.
        Sans cette étape, il est difficile de prioriser correctement les actions à mener.
      </p>

      <h2 style={s.h2}>Par où commencer</h2>
      <p>
        Trois priorités permettent d'avancer sans mobiliser des ressources disproportionnées : identifier vos
        actifs et données les plus critiques (ce qui, si vous le perdiez, aurait le plus d'impact),
        sécuriser les accès aux systèmes les plus sensibles, et sensibiliser vos équipes aux réflexes de base
        — la majorité des incidents commencent encore par une erreur humaine plutôt que par une faille
        technique sophistiquée. Un diagnostic structuré permet de transformer ces priorités générales en un
        plan d'action concret, adapté à votre situation.
      </p>
    </BlogArticleLayout>
  );
}

export default BlogArticleCybersecuritePME;
